/**
 * Diff Calculation Module
 *
 * Calculates differences between a snapshot and the current spreadsheet state.
 * Used to identify which cells were added, deleted, or changed.
 */

import type { CellSnapshot, SheetSnapshot, Snapshot } from './snapshot'
import type { TurnDeltaChain } from './snapshot-db'

// ============================================================================
// Types
// ============================================================================

export type DiffType =
  | 'added' // Cell exists now but not in snapshot (green)
  | 'deleted' // Cell existed in snapshot but not now (red)
  | 'formula-result-changed' // Formula same but result changed (yellow)
  | 'value-changed' // Value or formula changed (orange)
  | 'unchanged'

export interface CellDiff {
  address: string
  sheetId: string
  sheetName: string
  type: DiffType
  before: CellSnapshot | null
  after: CellSnapshot | null
}

export interface SheetDiff {
  sheetId: string
  sheetName: string
  hasChanges: boolean
  cells: CellDiff[]
}

export interface Diff {
  sheets: SheetDiff[]
  summary: {
    addedCells: number
    deletedCells: number
    changedCells: number
    sheetsWithChanges: number
  }
  deletedSheets: string[] // Names of sheets that were deleted since snapshot
  addedSheets: Array<{ id: string; name: string }> // Sheets that were added since snapshot
}

// ============================================================================
// Helper Functions
// ============================================================================
// Note: Excel utility functions (columnNumberToLetter, columnLetterToNumber, parseCellAddress)
// are imported from ../excel-utils

// ============================================================================
// Diff Calculation
// ============================================================================

/**
 * Calculate differences between two snapshots
 * Compares the "before" snapshot with the "after" snapshot
 */
export function calculateSnapshotDiff(
  beforeSnapshot: Snapshot,
  afterSnapshot: Snapshot
): Diff {
  const sheetDiffs: SheetDiff[] = []
  const deletedSheets: string[] = []
  const addedSheets: Array<{ id: string; name: string }> = []
  let totalAdded = 0
  let totalDeleted = 0
  let totalChanged = 0

  // Build maps for efficient lookup
  const beforeSheetMap = new Map<string, SheetSnapshot>()
  const afterSheetMap = new Map<string, SheetSnapshot>()

  for (const sheet of beforeSnapshot.sheets) {
    beforeSheetMap.set(sheet.id, sheet)
  }

  for (const sheet of afterSnapshot.sheets) {
    afterSheetMap.set(sheet.id, sheet)
  }

  // Check for added sheets (in after but not in before)
  for (const afterSheet of afterSnapshot.sheets) {
    if (!beforeSheetMap.has(afterSheet.id)) {
      // Also check by name to avoid false positives
      const foundByName = Array.from(beforeSheetMap.values()).find(
        (s) => s.name === afterSheet.name
      )
      if (!foundByName) {
        addedSheets.push({ id: afterSheet.id, name: afterSheet.name })
        const cells: CellDiff[] = []
        for (const [address, cellData] of Object.entries(afterSheet.cells)) {
          cells.push({
            address,
            sheetId: afterSheet.id,
            sheetName: afterSheet.name,
            type: 'added',
            before: null,
            after: cellData,
          })
          totalAdded++
        }
        sheetDiffs.push({
          sheetId: afterSheet.id,
          sheetName: afterSheet.name,
          hasChanges: cells.length > 0,
          cells,
        })
      }
    }
  }

  // Check for deleted and modified sheets
  for (const beforeSheet of beforeSnapshot.sheets) {
    const afterSheet = afterSheetMap.get(beforeSheet.id)
    const cells: CellDiff[] = []

    if (!afterSheet) {
      // Check by name in case sheet was recreated with new ID
      const foundByName = Array.from(afterSheetMap.values()).find(
        (s) => s.name === beforeSheet.name
      )
      if (!foundByName) {
        // Sheet was deleted
        deletedSheets.push(beforeSheet.name)
        for (const [address, cellData] of Object.entries(beforeSheet.cells)) {
          cells.push({
            address,
            sheetId: beforeSheet.id,
            sheetName: beforeSheet.name,
            type: 'deleted',
            before: cellData,
            after: null,
          })
          totalDeleted++
        }
        sheetDiffs.push({
          sheetId: beforeSheet.id,
          sheetName: beforeSheet.name,
          hasChanges: cells.length > 0,
          cells,
        })
        continue
      }
    }

    // Compare cells in the sheet
    const currentAfterSheet =
      afterSheet ||
      Array.from(afterSheetMap.values()).find(
        (s) => s.name === beforeSheet.name
      )

    if (currentAfterSheet) {
      // Build map of after cells
      const afterCells = new Map<string, CellSnapshot>()
      for (const [address, cellData] of Object.entries(
        currentAfterSheet.cells
      )) {
        afterCells.set(address, cellData)
      }

      // Check before cells against after
      for (const [address, beforeCell] of Object.entries(beforeSheet.cells)) {
        const afterCell = afterCells.get(address)

        if (!afterCell) {
          // Cell was deleted
          cells.push({
            address,
            sheetId: currentAfterSheet.id,
            sheetName: currentAfterSheet.name,
            type: 'deleted',
            before: beforeCell,
            after: null,
          })
          totalDeleted++
        } else if (
          afterCell.value !== beforeCell.value ||
          afterCell.formula !== beforeCell.formula
        ) {
          // Cell was changed
          const formulaChanged =
            afterCell.formula !== beforeCell.formula &&
            (beforeCell.formula || afterCell.formula)
          const valueChanged = afterCell.value !== beforeCell.value

          const diffType: DiffType =
            !formulaChanged &&
            beforeCell.formula &&
            afterCell.formula === beforeCell.formula &&
            valueChanged
              ? 'formula-result-changed'
              : 'value-changed'

          cells.push({
            address,
            sheetId: currentAfterSheet.id,
            sheetName: currentAfterSheet.name,
            type: diffType,
            before: beforeCell,
            after: afterCell,
          })
          totalChanged++
        }
        // If unchanged, we don't add it to the diff
        afterCells.delete(address) // Mark as processed
      }

      // Remaining after cells are new
      for (const [address, afterCell] of Array.from(afterCells.entries())) {
        cells.push({
          address,
          sheetId: currentAfterSheet.id,
          sheetName: currentAfterSheet.name,
          type: 'added',
          before: null,
          after: afterCell,
        })
        totalAdded++
      }

      sheetDiffs.push({
        sheetId: currentAfterSheet.id,
        sheetName: currentAfterSheet.name,
        hasChanges: cells.length > 0,
        cells,
      })
    }
  }

  return {
    sheets: sheetDiffs,
    summary: {
      addedCells: totalAdded,
      deletedCells: totalDeleted,
      changedCells: totalChanged,
      sheetsWithChanges: sheetDiffs.filter((s) => s.hasChanges).length,
    },
    deletedSheets,
    addedSheets,
  }
}

/**
 * Calculate a diff from a delta chain by reading ONLY the affected cells
 * from the current spreadsheet and comparing with the delta's beforeCells.
 *
 * This is much faster than a full snapshot diff since it only reads the
 * cells that were actually modified.
 *
 * @param chain The delta chain for the turn
 * @param readCurrentCells Function to read current cell values from the spreadsheet
 * @returns A Diff object, or null if the chain is incomplete
 */
export async function calculateDiffFromDeltas(
  chain: TurnDeltaChain,
  readCurrentCells: (
    cellKeys: string[]
  ) => Promise<Record<string, CellSnapshot | null>>
): Promise<Diff | null> {
  if (!chain.isComplete) return null

  // Collect all unique cell keys from all deltas
  const allCellKeys = new Set<string>()
  for (const delta of chain.deltas) {
    for (const key of Object.keys(delta.beforeCells)) {
      allCellKeys.add(key)
    }
  }

  if (allCellKeys.size === 0) {
    return {
      sheets: [],
      summary: {
        addedCells: 0,
        deletedCells: 0,
        changedCells: 0,
        sheetsWithChanges: 0,
      },
      deletedSheets: [],
      addedSheets: [],
    }
  }

  // Get the original pre-turn state (first delta wins for each cell)
  const beforeState = new Map<string, CellSnapshot>()
  for (const delta of chain.deltas) {
    for (const [key, cell] of Object.entries(delta.beforeCells)) {
      if (!beforeState.has(key)) {
        beforeState.set(key, cell)
      }
    }
  }

  // Read current state of those same cells
  const currentCells = await readCurrentCells(Array.from(allCellKeys))

  // Build diff grouped by sheet
  const sheetDiffsMap = new Map<
    string,
    { sheetName: string; cells: CellDiff[] }
  >()
  let totalAdded = 0
  let totalDeleted = 0
  let totalChanged = 0

  for (const key of allCellKeys) {
    const bangIdx = key.indexOf('!')
    if (bangIdx === -1) continue

    const sheetName = key.substring(0, bangIdx)
    const cellAddress = key.substring(bangIdx + 1)

    const beforeCell = beforeState.get(key) ?? null
    const afterCell = currentCells[key] ?? null

    if (!sheetDiffsMap.has(sheetName)) {
      sheetDiffsMap.set(sheetName, { sheetName, cells: [] })
    }
    const sheetDiff = sheetDiffsMap.get(sheetName)
    if (!sheetDiff) continue

    if (!beforeCell && afterCell) {
      sheetDiff.cells.push({
        address: cellAddress,
        sheetId: sheetName,
        sheetName,
        type: 'added',
        before: null,
        after: afterCell,
      })
      totalAdded++
    } else if (beforeCell && !afterCell) {
      sheetDiff.cells.push({
        address: cellAddress,
        sheetId: sheetName,
        sheetName,
        type: 'deleted',
        before: beforeCell,
        after: null,
      })
      totalDeleted++
    } else if (
      beforeCell &&
      afterCell &&
      (beforeCell.value !== afterCell.value ||
        beforeCell.formula !== afterCell.formula)
    ) {
      const formulaChanged =
        afterCell.formula !== beforeCell.formula &&
        (beforeCell.formula || afterCell.formula)
      const valueChanged = afterCell.value !== beforeCell.value

      const diffType: DiffType =
        !formulaChanged &&
        beforeCell.formula &&
        afterCell.formula === beforeCell.formula &&
        valueChanged
          ? 'formula-result-changed'
          : 'value-changed'

      sheetDiff.cells.push({
        address: cellAddress,
        sheetId: sheetName,
        sheetName,
        type: diffType,
        before: beforeCell,
        after: afterCell,
      })
      totalChanged++
    }
  }

  const sheetDiffs: SheetDiff[] = Array.from(sheetDiffsMap.values()).map(
    ({ sheetName, cells }) => ({
      sheetId: sheetName,
      sheetName,
      hasChanges: cells.length > 0,
      cells,
    })
  )

  return {
    sheets: sheetDiffs,
    summary: {
      addedCells: totalAdded,
      deletedCells: totalDeleted,
      changedCells: totalChanged,
      sheetsWithChanges: sheetDiffs.filter((s) => s.hasChanges).length,
    },
    deletedSheets: [],
    addedSheets: [],
  }
}
